import { Button, TextField } from '@pms-digital/design-system';
import { useRef, useState } from 'react';

/**
 * The secret URL in a read-only field, with « Copier l'URL » (§11.4, planche
 * `ReglagesPartage`, which draws it monospace - the package `TextField` has no
 * such face).
 *
 * ## `readOnly` and not `disabled`
 *
 * The rule `criteres/` states for a version opened in reading: a disabled
 * control leaves the tab order and its text stops being selectable - which
 * would make the ONE field of this screen whose whole purpose is to be copied
 * the only one that cannot be. It is read-only because §5.1 forbids editing the
 * secret, not because it is inert.
 *
 * ## The copy has a fallback, and the fallback is the field itself
 *
 * `navigator.clipboard` is absent on an insecure origin and can be refused by
 * permission, and this screen is opened over plain HTTP on a dev stack every
 * day. So the button SELECTS the field when the write fails, and says so: the
 * operator finishes with Ctrl+C rather than facing a button that did nothing.
 * A failed copy that looked like a success would be the worst of the three
 * outcomes - the URL is what the whole screen exists to hand over.
 */

export interface LinkUrlFieldProps {
  readonly url: string;
  readonly label: string;
}

export function LinkUrlField({ url, label }: LinkUrlFieldProps) {
  const field = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<'idle' | 'copied' | 'select'>('idle');

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setState('copied');
    } catch {
      // No clipboard, or refused: hand the operator the selection instead.
      field.current?.select();
      setState('select');
    }
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <TextField
            ref={field}
            label={label}
            hideLabel
            compact
            type="text"
            readOnly
            data-testid="partage-lien-url"
            value={url}
            onFocus={(event) => event.currentTarget.select()}
          />
        </div>
        <Button
          type="button"
          variant="accent"
          size="sm"
          icon={state === 'copied' ? 'check' : 'content_copy'}
          data-testid="partage-lien-copier"
          onClick={() => void copy()}
        >
          Copier l’URL
        </Button>
      </div>
      {/* Mounted from the first render so the message is announced when it
          arrives, and worded for the outcome that actually happened. */}
      <p aria-live="polite" className="text-gray-600 text-caption">
        {state === 'copied' ? 'URL copiée.' : null}
        {state === 'select' ? 'Le presse-papiers est indisponible : l’URL est sélectionnée.' : null}
      </p>
    </div>
  );
}
